/**
 * NetworkManager - Client-side WebSocket handler for multiplayer
 */

import { io, Socket } from 'socket.io-client';
import type { EventBus } from '@/core/EventBus';
import type {
  ClientMessage,
  ServerMessage,
  GamePhase,
} from './messages';
import type { AxialCoord } from '@/types/hex.types';

/**
 * Network events emitted to the game
 */
export const NetworkEvents = {
  CONNECTED: 'network:connected',
  DISCONNECTED: 'network:disconnected',
  QUEUE_JOINED: 'network:queueJoined',
  MATCH_FOUND: 'network:matchFound',
  GAME_START: 'network:gameStart',
  PHASE_CHANGE: 'network:phaseChange',
  SHOP_REFRESH: 'network:shopRefresh',
  PURCHASE_RESULT: 'network:purchaseResult',
  REROLL_RESULT: 'network:rerollResult',
  OPPONENT_BOARD: 'network:opponentBoard',
  ROUND_RESULT: 'network:roundResult',
  MATCH_RESULT: 'network:matchResult',
  OPPONENT_DISCONNECTED: 'network:opponentDisconnected',
  OPPONENT_RECONNECTED: 'network:opponentReconnected',
  ERROR: 'network:error',
} as const;

export interface NetworkState {
  connected: boolean;
  inQueue: boolean;
  inGame: boolean;
  roomId: string | null;
  playerId: 1 | 2 | null;
  currentPhase: GamePhase;
  roundNumber: number;
}

// In production, connect to same origin (empty string makes socket.io use current host)
const SERVER_URL = import.meta.env.VITE_SERVER_URL ||
  (import.meta.env.PROD ? '' : 'http://localhost:3001');

export class NetworkManager {
  private socket: Socket | null = null;
  private eventBus: EventBus;
  private state: NetworkState = {
    connected: false,
    inQueue: false,
    inGame: false,
    roomId: null,
    playerId: null,
    currentPhase: 'waiting',
    roundNumber: 0,
  };

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  /**
   * Connect to the game server
   */
  connect(): void {
    if (this.socket?.connected) return;

    this.socket = io(SERVER_URL, {
      path: '/socket.io/',
      transports: ['polling', 'websocket'], // Start with polling, upgrade to websocket
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
    });

    this.socket.on('connect', () => {
      console.log('[Network] Connected to server');
      this.state.connected = true;
      this.eventBus.emit(NetworkEvents.CONNECTED, {});
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('[Network] Disconnected:', reason);
      this.state.connected = false;
      this.eventBus.emit(NetworkEvents.DISCONNECTED, { reason });
    });

    this.socket.on('message', (message: ServerMessage) => {
      this.handleServerMessage(message);
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('[Network] Connection error:', error.message);
    });
  }

  /**
   * Disconnect from the server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.resetState();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.state.connected;
  }

  /**
   * Check if in multiplayer game
   */
  isInGame(): boolean {
    return this.state.inGame;
  }

  /**
   * Get current network state
   */
  getState(): Readonly<NetworkState> {
    return this.state;
  }

  /**
   * Get local player ID (1 or 2)
   */
  getPlayerId(): 1 | 2 | null {
    return this.state.playerId;
  }

  /**
   * Join the matchmaking queue
   * @param playerName Optional player name to send to server
   */
  joinQueue(playerName?: string): void {
    this.send({ type: 'join_queue', playerName });
  }

  /**
   * Leave the matchmaking queue
   */
  leaveQueue(): void {
    this.send({ type: 'leave_queue' });
    this.state.inQueue = false;
  }

  /**
   * Signal ready for battle
   */
  ready(): void {
    this.send({ type: 'ready' });
  }

  /**
   * Send unit placement to server
   */
  placeUnit(unitType: string, coord: AxialCoord, starLevel: number): void {
    this.send({
      type: 'place_unit',
      unitType,
      coord,
      starLevel,
    });
  }

  /**
   * Send unit removal to server
   */
  removeUnit(coord: AxialCoord): void {
    this.send({
      type: 'remove_unit',
      coord,
    });
  }

  /**
   * Request to buy a unit from shop
   */
  buyUnit(slotIndex: number, targetCoord?: AxialCoord): void {
    this.send({
      type: 'buy_unit',
      slotIndex,
      targetCoord,
    });
  }

  /**
   * Request shop reroll
   */
  rerollShop(): void {
    this.send({ type: 'reroll_shop' });
  }

  /**
   * Lock or unlock shop
   */
  lockShop(locked: boolean): void {
    this.send({
      type: 'lock_shop',
      locked,
    });
  }

  /**
   * Request early battle start
   */
  startBattle(): void {
    this.send({ type: 'start_battle' });
  }

  /**
   * Report battle result to server
   */
  reportBattleResult(winnerId: 0 | 1 | 2): void {
    this.send({ type: 'battle_result', winnerId });
  }

  /**
   * Send message to server
   */
  private send(message: ClientMessage): void {
    if (this.socket?.connected) {
      this.socket.emit('message', message);
    } else {
      console.warn('[Network] Cannot send message - not connected');
    }
  }

  /**
   * Handle incoming server messages
   */
  private handleServerMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'queue_joined':
        this.state.inQueue = true;
        this.eventBus.emit(NetworkEvents.QUEUE_JOINED, {
          position: message.position,
        });
        break;

      case 'match_found':
        this.state.inQueue = false;
        this.state.inGame = true;
        this.state.roomId = message.roomId;
        this.state.playerId = message.playerId;
        this.eventBus.emit(NetworkEvents.MATCH_FOUND, {
          roomId: message.roomId,
          playerId: message.playerId,
          opponentName: message.opponentName,
        });
        break;

      case 'game_start':
        this.eventBus.emit(NetworkEvents.GAME_START, {
          poolState: message.poolState,
          startingGold: message.startingGold,
        });
        break;

      case 'phase_change':
        this.state.currentPhase = message.phase;
        this.state.roundNumber = message.roundNumber;
        this.eventBus.emit(NetworkEvents.PHASE_CHANGE, {
          phase: message.phase,
          roundNumber: message.roundNumber,
          combatSeed: message.combatSeed,
          prepTime: message.prepTime,
          opponentBoard: message.opponentBoard,
        });
        break;

      case 'shop_refresh':
        this.eventBus.emit(NetworkEvents.SHOP_REFRESH, {
          slots: message.slots,
          gold: message.gold,
        });
        break;

      case 'purchase_result':
        this.eventBus.emit(NetworkEvents.PURCHASE_RESULT, {
          success: message.success,
          slotIndex: message.slotIndex,
          unitType: message.unitType,
          newGold: message.newGold,
          error: message.error,
        });
        break;

      case 'reroll_result':
        this.eventBus.emit(NetworkEvents.REROLL_RESULT, {
          success: message.success,
          slots: message.slots,
          newGold: message.newGold,
          error: message.error,
        });
        break;

      case 'opponent_board':
        this.eventBus.emit(NetworkEvents.OPPONENT_BOARD, {
          units: message.units,
        });
        break;

      case 'round_result':
        this.eventBus.emit(NetworkEvents.ROUND_RESULT, {
          winnerId: message.winnerId,
          scores: message.scores,
          goldAwarded: message.goldAwarded,
        });
        break;

      case 'match_result':
        this.state.inGame = false;
        this.eventBus.emit(NetworkEvents.MATCH_RESULT, {
          winnerId: message.winnerId,
          finalScores: message.finalScores,
        });
        break;

      case 'opponent_disconnected':
        this.eventBus.emit(NetworkEvents.OPPONENT_DISCONNECTED, {
          timeout: message.timeout,
        });
        break;

      case 'opponent_reconnected':
        this.eventBus.emit(NetworkEvents.OPPONENT_RECONNECTED, {});
        break;

      case 'error':
        console.error('[Network] Server error:', message.message);
        this.eventBus.emit(NetworkEvents.ERROR, {
          message: message.message,
          code: message.code,
        });
        break;
    }
  }

  /**
   * Reset state to defaults
   */
  private resetState(): void {
    this.state = {
      connected: false,
      inQueue: false,
      inGame: false,
      roomId: null,
      playerId: null,
      currentPhase: 'waiting',
      roundNumber: 0,
    };
  }
}
